import Vue from 'vue'
import VueRouter from 'vue-router'
import useAuth from '@/composables/useAuth'
import { USER_ROLES } from '@/enums/enums'
import isTheFlowEnabled from '@/utils/featureFlags'

Vue.use(VueRouter)

const baseRoutes = [
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/auth/logout',
    name: 'logout',
    component: () => import('@/views/auth/Logout.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/no-academy',
    name: 'no-academy',
    component: () => import('@/views/auth/NoAcademy.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/pending-approval',
    name: 'pending-approval',
    component: () => import('@/views/auth/PendingApproval.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/',
    name: 'home',
    redirect: 'dashboard',
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/dashboard/Dashboard.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/fitness-exercises',
    name: 'fitness-exercises',
    component: () => import('@/views/exercises/FitnessExercises.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/practice-drills',
    name: 'practice-drills',
    component: () => import('@/views/exercises/PracticeDrills.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/flow-exercises',
    name: 'flow-exercises',
    component: () => import('@/views/exercises/FlowExercises.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH], feature: 'the-flow' },
  },
  {
    path: '/fitness-routines',
    name: 'fitness-routines',
    component: () => import('@/views/fitnessRoutines/FitnessRoutines.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/flow-sessions',
    name: 'flow-sessions',
    component: () => import('@/views/flowSessions/FlowSessions.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH], feature: 'the-flow' },
  },
  {
    path: '/practice-sessions',
    name: 'practice-sessions',
    component: () => import('@/views/practiceSessions/PracticeSessions.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/questionnaires',
    name: 'questionnaires',
    component: () => import('@/views/questionnaires/Questionnaires.vue'),
    meta: { authorize: [USER_ROLES.ADMIN] },
  },
  {
    path: '/training-plans',
    name: 'training-plans',
    component: () => import('@/views/trainingPlans/TrainingPlans.vue'),
    meta: { authorize: [USER_ROLES.ADMIN] },
  },
  {
    path: '/content-packs',
    name: 'content-packs',
    component: () => import('@/views/contentPacks/ContentPacks.vue'),
    meta: { authorize: [USER_ROLES.ADMIN] },
  },
  {
    path: '/fitness-assessments',
    name: 'fitness-assessments',
    component: () => import('@/views/fitnessAssessments/FitnessAssessments.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/flow-assessments',
    name: 'flow-assessments',
    component: () => import('@/views/flowAssessments/FlowAssessments.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH], feature: 'the-flow' },
  },
  {
    path: '/golf-skills-assessments',
    name: 'golf-skills-assessments',
    component: () => import('@/views/golfSkillsAssessments/GolfSkillsAssessments.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/academies',
    name: 'academies',
    component: () => import('@/views/academies/Academies.vue'),
    meta: { authorize: [USER_ROLES.ADMIN] },
  },
  {
    path: '/manage-coaches',
    name: 'manage-coaches',
    component: () => import('@/views/coaches/ManageCoaches.vue'),
    meta: { title: 'Manage Users', authorize: [USER_ROLES.ADMIN] },
  },
  {
    path: '/reports',
    name: 'reports',
    component: () => import('@/views/reports/Reports.vue'),
    meta: { authorize: [USER_ROLES.ADMIN] },
  },
  {
    path: '/my-academies',
    name: 'my-academies',
    component: () => import('@/views/academies/MyAcademies.vue'),
    meta: { authorize: [USER_ROLES.COACH] },
  },
  {
    path: '/academies/:id',
    name: 'academy-details',
    component: () => import('@/views/academies/partials/AcademyDetails.vue'),
    meta: { authorize: [USER_ROLES.ADMIN, USER_ROLES.COACH] },
  },
  {
    path: '/error-403',
    name: 'error-403',
    component: () => import('@/views/Error403.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '/error-404',
    name: 'error-404',
    component: () => import('@/views/Error.vue'),
    meta: { layout: 'blank' },
  },
  {
    path: '*',
    redirect: 'error-404',
  },
]

const routes = baseRoutes.filter(route => route.meta?.feature !== 'the-flow' || isTheFlowEnabled)

const { hasRole, state: auth } = useAuth()
const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

// eslint-disable-next-line consistent-return
router.beforeEach(async (to, from, next) => {
  const { authorize } = to.meta

  // Allow access to login, logout and pending-approval pages
  if (to.name === 'login' || to.name === 'logout' || to.name === 'pending-approval') {
    return next()
  }

  // Check user approval status for logged in users
  if (auth.user && to.name !== 'pending-approval') {
    try {
      const { getUserAcademy } = require('@/composables/api/academies').default()
      const userData = await getUserAcademy()

      // If user status is not APPROVED, redirect to pending approval page
      if (userData.status && userData.status !== 'APPROVED') {
        return next({ name: 'pending-approval' })
      }
    } catch (error) {
      console.error('Error checking user approval status:', error)
    }
  }

  // Special validation for no-academy page - only for coaches without academies
  if (to.name === 'no-academy') {
    // Only logged in users can access this page
    if (!auth.user) {
      return next({ name: 'login', query: { redirectTo: to.fullPath } })
    }

    // Non-coaches should be redirected to home
    if (!hasRole(['COACH'])) {
      return next({ name: 'home' })
    }

    // Coaches with academies should be redirected to dashboard
    try {
      const { getUserAcademy } = require('@/composables/api/academies').default()
      const userAcademy = await getUserAcademy()
      const userCompanyIds = Array.isArray(userAcademy.companyId) ? userAcademy.companyId : (userAcademy.companyId ? [userAcademy.companyId] : [])

      if (userCompanyIds.some(id => id)) {
        return next({ name: 'dashboard' })
      }

      // If coach has no academies, allow access to no-academy page
    } catch (error) {
      console.error('Error checking coach academy access for no-academy:', error)

      // If error occurred, allow access to no-academy page for coaches
    }

    return next()
  }

  if (authorize) {
    if (to.name !== 'login' && !auth.user) {
      return next({ name: 'login', query: { redirectTo: to.fullPath } })
    }

    if (!hasRole(authorize)) {
      return next({ name: 'error-403', query: { redirectTo: to.fullPath } })
    }

    // Check if coach has academy access
    if (hasRole(['COACH']) && to.name !== 'no-academy') {
      try {
        const { getUserAcademy } = require('@/composables/api/academies').default()
        const userAcademy = await getUserAcademy()
        const userCompanyIds = Array.isArray(userAcademy.companyId) ? userAcademy.companyId : (userAcademy.companyId ? [userAcademy.companyId] : [])

        // Check if coach has no academies or empty companyId array
        if (!userCompanyIds.some(id => id)) {
          return next({ name: 'no-academy' })
        }
      } catch (error) {
        console.error('Error checking coach academy access:', error)

        // A failed request cannot be treated as proof that the Coach has no academy.
        if (!error.response) return next()

        return next({ name: 'no-academy' })
      }
    }

    if (to.name === 'academy-details' && hasRole(['COACH'])) {
      try {
        const { getUserAcademy } = require('@/composables/api/academies').default()
        const userAcademy = await getUserAcademy()

        const userCompanyIds = Array.isArray(userAcademy.companyId) ? userAcademy.companyId : (userAcademy.companyId ? [userAcademy.companyId] : [])

        if (!userCompanyIds.includes(to.params.id)) {
          return next({ name: 'error-403', query: { redirectTo: to.fullPath } })
        }
      } catch (error) {
        console.error('Error during academy validation:', error)

        // Keep the current navigation during a temporary connectivity problem.
        if (!error.response) return next()

        return next({ name: 'error-403', query: { redirectTo: to.fullPath } })
      }
    }
  }

  next()
})
export default router
